import {compact, max} from 'lodash';
import {DateTime} from 'luxon';

import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {ExtendedError} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {reportToBugsnag} from '../../../../../srcCommon/services/bugsnag/bugsnag';
import {TrueAvailabilityStatusesEnum} from '../../../../helpers/models/schedulingLinkAvailabilityHelpers';
import {ActionWithPayload, batch} from '../../../../helpers/reduxHelpers';
import {
  DraftSchedulingLinkModel,
  isSavedSchedulingLink,
  SchedulingLinkAvailabilityModel,
  SchedulingLinkAvailabilityTypesEnum,
  SchedulingLinkMeetingPropertiesModel,
  SchedulingLinkModel,
  SchedulingLinkRepeatingAvailabilityBlockModel,
  SchedulingLinkStandardAvailabilityBlockModel,
} from '../../../../models/api/schedulingLinkModel';
import {HttpNetworkError, HttpResponseError, HttpTimeoutError} from '../../../../services/http/httpError';
import {ThunkAction, ThunkAsyncAction} from '../../../appThunk';
import {fetchCalendarVersionAsyncAction} from '../../../data/calendarVersions/calendarVersionsActions';
import {
  getComposeCalendarVersion,
  getWriteAccessCalendarVersionIds,
  makeGetCalendarData,
} from '../../../data/calendarVersions/calendarVersionsSelectors';
import {getCalendarVersions} from '../../../data/dataSelectors';
import {updateCalendarTimezoneAction} from '../../../data/preferences/preferencesActions';
import {getCalendarTimezone} from '../../../data/preferences/preferencesSelectors';
import {
  createSchedulingLinkAsyncAction,
  fetchSchedulingLinkAsyncAction,
  fetchSchedulingLinkAvailabilityAsyncAction,
  updateSchedulingLinkAsyncAction,
} from '../../../data/schedulingLinks/schedulingLinksActions';
import {makeGetSchedulingLink} from '../../../data/schedulingLinks/schedulingLinksSelectors';
import {PanelActionPayload} from '../../panelsActions';
import {getPanel} from '../../panelsSelectors';
import {CalendarsPanelId, ComposerPanelId, ViewerPanelId} from '../../panelStateBase';
import {
  addViewingNowCalendarVersionForParticipantAction,
  findOrCreateViewingNowCalendar,
  navigateCalendarsAction,
  selectCalendarVersionAction,
  unselectCalendarVersionAction,
  updateSelectedCalendarVersionAction,
} from '../calendarsPanelActions';
import {makeGetCalendarOrganizerAddress} from '../calendarsPanelSelectors';
import {CalendarsPanelState} from '../calendarsPanelState';
import {CalendarsViewTypesEnum} from '../calendarsParameters';
import {getDraftSchedulingLink, getSelectedStandardSlots} from './calendarsPanelSchedulingSelectors';

/*
 * Type enum.
 */

export enum CalendarsPanelSchedulingActionsEnum {
  UPDATE_TRUE_AVAILABILITY_STATUS = '@@panels/calendars/scheduling/UPDATE_TRUE_AVAILABILITY_STATUS',
  UPDATE_ORGANIZER_ADDRESS = '@@panels/calendars/scheduling/UPDATE_ORGANIZER_ADDRESS',
  UPDATE_DRAFT_LINK = '@@panels/calendars/scheduling/UPDATE_DRAFT_LINK',
  UPDATE_MEETING_PROPERTIES = '@@panels/calendars/scheduling/UPDATE_MEETING_PROPERTIES',
  UPDATE_MEETING_DURATION = '@@panels/calendars/scheduling/UPDATE_MEETING_DURATION',
  ADD_ADDITIONAL_GUESTS = '@@panels/calendars/scheduling/ADD_ADDITIONAL_GUESTS',
  FETCH_SCHEDULING_LINK = '@@panels/calendars/scheduling/FETCH_SCHEDULING_LINK',
  FETCH_SCHEDULING_LINK_COMPLETED = '@@panels/calendars/scheduling/FETCH_SCHEDULING_LINK_COMPLETED',
  FETCH_SCHEDULING_LINK_FAILED = '@@panels/calendars/scheduling/FETCH_SCHEDULING_LINK_FAILED',
}

/*
 * Types.
 */

type CalendarsPanelActionPayload = PanelActionPayload<CalendarsPanelId>;

interface UpdateTrueAvailabilityStatusActionPayload extends CalendarsPanelActionPayload {
  status: TrueAvailabilityStatusesEnum;
}

interface UpdateTrueAvailabilityStatusAction
  extends ActionWithPayload<UpdateTrueAvailabilityStatusActionPayload> {
  type: CalendarsPanelSchedulingActionsEnum.UPDATE_TRUE_AVAILABILITY_STATUS;
}

interface UpdateOrganizerAddressActionPayload extends CalendarsPanelActionPayload {
  organizerAddress: string;
}

interface UpdateOrganizerAddressAction extends ActionWithPayload<UpdateOrganizerAddressActionPayload> {
  type: CalendarsPanelSchedulingActionsEnum.UPDATE_ORGANIZER_ADDRESS;
}

interface UpdateDraftSchedulingLinkActionPayload extends CalendarsPanelActionPayload {
  updatedLink: Partial<DraftSchedulingLinkModel>;
}

interface UpdateDraftSchedulingLinkAction extends ActionWithPayload<UpdateDraftSchedulingLinkActionPayload> {
  type: CalendarsPanelSchedulingActionsEnum.UPDATE_DRAFT_LINK;
}

interface UpdateDraftMeetingPropertiesActionPayload extends CalendarsPanelActionPayload {
  updatedProperties: Partial<SchedulingLinkMeetingPropertiesModel>;
}

interface UpdateDraftMeetingPropertiesAction
  extends ActionWithPayload<UpdateDraftMeetingPropertiesActionPayload> {
  type: CalendarsPanelSchedulingActionsEnum.UPDATE_MEETING_PROPERTIES;
}

interface UpdateDraftMeetingDurationActionPayload extends CalendarsPanelActionPayload {
  durationMinutes: number;
}

interface UpdateDraftMeetingDurationAction
  extends ActionWithPayload<UpdateDraftMeetingDurationActionPayload> {
  type: CalendarsPanelSchedulingActionsEnum.UPDATE_MEETING_DURATION;
}

interface AddAdditionalGuestsActionPayload extends CalendarsPanelActionPayload {
  newGuests: ReadonlyArray<string>;
}

interface AddAdditionalGuestsAction extends ActionWithPayload<AddAdditionalGuestsActionPayload> {
  type: CalendarsPanelSchedulingActionsEnum.ADD_ADDITIONAL_GUESTS;
}

interface FetchSchedulingLinkAction extends ActionWithPayload<CalendarsPanelActionPayload> {
  type: CalendarsPanelSchedulingActionsEnum.FETCH_SCHEDULING_LINK;
}

interface FetchSchedulingLinkCompletedActionPayload extends CalendarsPanelActionPayload {
  draftLink: DraftSchedulingLinkModel;
}

interface FetchSchedulingLinkCompletedAction
  extends ActionWithPayload<FetchSchedulingLinkCompletedActionPayload> {
  type: CalendarsPanelSchedulingActionsEnum.FETCH_SCHEDULING_LINK_COMPLETED;
}

interface FetchSchedulingLinkFailedAction extends ActionWithPayload<CalendarsPanelActionPayload> {
  type: CalendarsPanelSchedulingActionsEnum.FETCH_SCHEDULING_LINK_FAILED;
}

/*
 * Action creators.
 */

export function updateTrueAvailabilityStatusAction(
  panelId: CalendarsPanelId,
  status: TrueAvailabilityStatusesEnum,
): UpdateTrueAvailabilityStatusAction {
  return {
    type: CalendarsPanelSchedulingActionsEnum.UPDATE_TRUE_AVAILABILITY_STATUS,
    payload: {panelId, status},
  };
}

export function updateOrganizerAddressAction(
  panelId: CalendarsPanelId,
  organizerAddress: string,
): UpdateOrganizerAddressAction {
  return {
    type: CalendarsPanelSchedulingActionsEnum.UPDATE_ORGANIZER_ADDRESS,
    payload: {panelId, organizerAddress},
  };
}

export function updateDraftSchedulingLinkAction(
  panelId: CalendarsPanelId,
  updatedLink: Partial<DraftSchedulingLinkModel>,
): UpdateDraftSchedulingLinkAction {
  return {
    type: CalendarsPanelSchedulingActionsEnum.UPDATE_DRAFT_LINK,
    payload: {panelId, updatedLink},
  };
}

export function updateMeetingPropertiesAction(
  panelId: CalendarsPanelId,
  updatedProperties: Partial<SchedulingLinkMeetingPropertiesModel>,
): UpdateDraftMeetingPropertiesAction {
  return {
    type: CalendarsPanelSchedulingActionsEnum.UPDATE_MEETING_PROPERTIES,
    payload: {panelId, updatedProperties},
  };
}

export function updateMeetingDurationAction(
  panelId: CalendarsPanelId,
  durationMinutes: number,
): UpdateDraftMeetingDurationAction {
  return {
    type: CalendarsPanelSchedulingActionsEnum.UPDATE_MEETING_DURATION,
    payload: {panelId, durationMinutes},
  };
}

export function addAdditionalGuestsAction(
  panelId: CalendarsPanelId,
  newGuests: ReadonlyArray<string>,
): ThunkAction {
  return (dispatch) => {
    batch(() => {
      const action: AddAdditionalGuestsAction = {
        type: CalendarsPanelSchedulingActionsEnum.ADD_ADDITIONAL_GUESTS,
        payload: {panelId, newGuests},
      };
      dispatch(action);

      newGuests.forEach((address) =>
        dispatch(addViewingNowCalendarVersionForParticipantAction(panelId, address)),
      );
    });
  };
}

export function fetchSchedulingLinkAction(panelId: CalendarsPanelId): FetchSchedulingLinkAction {
  return {
    type: CalendarsPanelSchedulingActionsEnum.FETCH_SCHEDULING_LINK,
    payload: {panelId},
  };
}

export function fetchSchedulingLinkCompletedAction(
  panelId: CalendarsPanelId,
  draftLink: DraftSchedulingLinkModel,
): FetchSchedulingLinkCompletedAction {
  return {
    type: CalendarsPanelSchedulingActionsEnum.FETCH_SCHEDULING_LINK_COMPLETED,
    payload: {panelId, draftLink},
  };
}

export function fetchSchedulingLinkFailedAction(panelId: CalendarsPanelId): FetchSchedulingLinkFailedAction {
  return {
    type: CalendarsPanelSchedulingActionsEnum.FETCH_SCHEDULING_LINK_FAILED,
    payload: {panelId},
  };
}

export function updateStandardAvailabilityAction(
  panelId: CalendarsPanelId,
  availabilityBlocks: ReadonlyArray<SchedulingLinkStandardAvailabilityBlockModel>,
): ThunkAction {
  return async (dispatch) => {
    batch(() => {
      // Reset the availability.
      dispatch(updateTrueAvailabilityStatusAction(panelId, TrueAvailabilityStatusesEnum.UNKNOWN));

      // Update the scheduling link.
      dispatch(
        updateDraftSchedulingLinkAction(panelId, {
          availability: {
            type: SchedulingLinkAvailabilityTypesEnum.STANDARD,
            blocks: availabilityBlocks,
          },
        }),
      );
    });
  };
}

export function updateRepeatingAvailabilityAction(
  panelId: CalendarsPanelId,
  availabilityBlocks: ReadonlyArray<SchedulingLinkRepeatingAvailabilityBlockModel>,
): ThunkAction {
  return async (dispatch) => {
    batch(() => {
      // Reset the availability.
      dispatch(updateTrueAvailabilityStatusAction(panelId, TrueAvailabilityStatusesEnum.UNKNOWN));

      // Update the scheduling link.
      dispatch(
        updateDraftSchedulingLinkAction(panelId, {
          availability: {
            type: SchedulingLinkAvailabilityTypesEnum.REPEATING,
            blocks: availabilityBlocks,
          },
        }),
      );
    });
  };
}

export function createSchedulingCalendarsPanelTransitionAction(
  panelId: CalendarsPanelId,
  parentPanelId: CalendarsPanelId | ComposerPanelId | ViewerPanelId,
  schedulingLinkId: number | undefined,
  draftLinkOverrides: Partial<DraftSchedulingLinkModel>,
  panelOverrides: Partial<CalendarsPanelState> = {},
): ThunkAction {
  return schedulingLinkId
    ? createPanelTransitionActionForExistingLink(panelId, parentPanelId, schedulingLinkId, panelOverrides)
    : createPanelTransitionActionForNewLink(panelId, parentPanelId, draftLinkOverrides, panelOverrides);
}

function createPanelTransitionActionForNewLink(
  panelId: CalendarsPanelId,
  parentPanelId: CalendarsPanelId | ComposerPanelId | ViewerPanelId,
  draftLinkOverrides: Partial<DraftSchedulingLinkModel>,
  panelOverrides: Partial<CalendarsPanelState> = {},
): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const composeCalendarVersion = getComposeCalendarVersion(state);
    const calendarVersionId = composeCalendarVersion?.id;

    if (!calendarVersionId) {
      throw new Error('Missing calendarVersionId');
    }

    const parentPanel = getPanel(state, {panelId: parentPanelId});
    if (!parentPanel) {
      throw new Error('Missing parent panel');
    }

    const getCalendarOrganizerAddress = makeGetCalendarOrganizerAddress(() => calendarVersionId);
    const organizerAddress = getCalendarOrganizerAddress(state, undefined) || '';
    const {shouldForcePopout} = parentPanel;
    const draftLink = buildDraftSchedulingLink({...draftLinkOverrides, calendarVersionId});

    dispatch(
      navigateCalendarsAction(
        panelId,
        {
          viewType: CalendarsViewTypesEnum.SCHEDULING,
          selectedCalendarVersionIds: [calendarVersionId],
        },
        {
          scheduling: {
            draftLink,
            trueAvailabilityStatus: TrueAvailabilityStatusesEnum.UNKNOWN,
            organizerAddress,
            didEditDraft: false,
            status: AsyncStatusesEnum.LOADED,
          },
          shouldForcePopout,
          ...panelOverrides,
        },
      ),
    );

    return panelId;
  };
}

function createPanelTransitionActionForExistingLink(
  panelId: CalendarsPanelId,
  parentPanelId: CalendarsPanelId | ComposerPanelId | ViewerPanelId,
  schedulingLinkId: number,
  panelOverrides: Partial<CalendarsPanelState> = {},
): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();

    const schedulingLink = makeGetSchedulingLink(() => schedulingLinkId)(state);
    if (!schedulingLink) {
      throw new Error('Missing schedulingLink');
    }

    const getCalendarOrganizerAddress = makeGetCalendarOrganizerAddress(
      () => schedulingLink.calendarVersionId,
    );
    const organizerAddress = getCalendarOrganizerAddress(state, undefined) || '';

    const parentPanel = getPanel(state, {panelId: parentPanelId});
    if (!parentPanel) {
      throw new Error('Missing parent panel');
    }

    const {shouldForcePopout} = parentPanel;
    dispatch(
      navigateCalendarsAction(
        panelId,
        {
          viewType: CalendarsViewTypesEnum.SCHEDULING,
          // Default to no selectedCalendarVersionIds, since we leave it up
          // to fetchDraftSchedulingLink to update this state.
          selectedCalendarVersionIds: [],
        },
        {
          scheduling: {
            draftLink: schedulingLink,
            trueAvailabilityStatus: TrueAvailabilityStatusesEnum.UNKNOWN,
            organizerAddress,
            didEditDraft: false,
            status: AsyncStatusesEnum.LOADING,
          },
          shouldForcePopout,
          ...panelOverrides,
        },
      ),
    );

    dispatch(fetchDraftSchedulingLink(panelId, schedulingLinkId));
  };
}

function fetchDraftSchedulingLink(panelId: CalendarsPanelId, linkId: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    try {
      dispatch(fetchSchedulingLinkAction(panelId));

      const schedulingLink = await dispatch(fetchSchedulingLinkAsyncAction(linkId));

      const state = getState();
      const calendarVersions = getCalendarVersions(state);
      const writeAccessCalendarVersionIds = getWriteAccessCalendarVersionIds(state);

      const newDoubleBookingCalendarVersions = compact(
        await Promise.all(
          schedulingLink.doubleBookingCalendarVersions.map(async (calendarVersionId) => {
            if (calendarVersions[calendarVersionId]) {
              // We have this calendar version. Keep it.
              return calendarVersionId;
            }

            try {
              // We don't have this calendar version. Fetch it.
              await dispatch(fetchCalendarVersionAsyncAction(calendarVersionId));
            } catch (error) {
              if (error instanceof HttpResponseError && error.status === 404) {
                // This calendar version no longer exists. Remove it.
                return undefined;
              }
            }
            return calendarVersionId;
          }),
        ),
      );

      const [nextWriteAccessCalendarVersionId] = writeAccessCalendarVersionIds;
      const newCalendarVersionId = writeAccessCalendarVersionIds.includes(schedulingLink.calendarVersionId)
        ? schedulingLink.calendarVersionId
        : nextWriteAccessCalendarVersionId;

      if (!newCalendarVersionId) {
        throw new Error('Unable to choose another scheduling calendar.');
      }

      const updatedSchedulingLink = {
        ...schedulingLink,
        doubleBookingCalendarVersions: newDoubleBookingCalendarVersions,
        calendarVersionId: newCalendarVersionId,
      };

      batch(() => {
        // Update the panel state.
        updatedSchedulingLink.doubleBookingCalendarVersions.forEach((calendarVersionId) => {
          const {calendarVersion, calendar} = makeGetCalendarData(() => calendarVersionId)(state, {});

          // If the calendarVersion is hidden, add it to the viewingNowCalendar section.
          // Otherwise, select the calendarVersion.
          if (calendarVersion.isHidden) {
            dispatch(
              findOrCreateViewingNowCalendar(panelId, calendar.organizerAddress || '', calendarVersion),
            );
          } else {
            dispatch(updateSelectedCalendarVersionAction(panelId, calendarVersion.id, true, false));
          }
        });

        // Update the schedulingLink loading status.
        dispatch(fetchSchedulingLinkCompletedAction(panelId, updatedSchedulingLink));
      });
    } catch (error) {
      dispatch(fetchSchedulingLinkFailedAction(panelId));

      if (error instanceof HttpNetworkError || error instanceof HttpTimeoutError) {
        return;
      }

      reportToBugsnag(new ExtendedError('Failed to fetch draft scheduling link.', error));
    }
  };
}

export function selectSchedulingCalendarVersionAction(
  panelId: CalendarsPanelId,
  calendarVersionId: number,
): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const draftLink = getDraftSchedulingLink(state, {panelId});
    const previousCalendarVersionId = draftLink.calendarVersionId;

    const getCalendarOrganizerAddress = makeGetCalendarOrganizerAddress(() => calendarVersionId);
    const organizerAddress = getCalendarOrganizerAddress(state, undefined) || '';

    batch(() => {
      // Update the scheduling link.
      dispatch(updateDraftSchedulingLinkAction(panelId, {calendarVersionId}));

      // Update the organizer address.
      dispatch(updateOrganizerAddressAction(panelId, organizerAddress));

      // Unselect the previous calendar version.
      dispatch(unselectCalendarVersionAction(panelId, previousCalendarVersionId, false));

      // Select the current calendar version.
      dispatch(selectCalendarVersionAction(panelId, calendarVersionId, false));
    });
  };
}

export interface InsertMeetingTimesAsyncActionResult {
  hasAvailability: boolean;
  availability?: SchedulingLinkAvailabilityModel;
  schedulingLink?: SchedulingLinkModel;
}
export function insertMeetingTimesAsyncAction(
  panelId: CalendarsPanelId,
): ThunkAsyncAction<InsertMeetingTimesAsyncActionResult> {
  return async (dispatch, getState) => {
    const state = getState();
    const selectedSlots = getSelectedStandardSlots(state, {panelId});
    const draftLink = getDraftSchedulingLink(state, {panelId});

    try {
      dispatch(updateTrueAvailabilityStatusAction(panelId, TrueAvailabilityStatusesEnum.LOADING));
      const schedulingLink = await dispatch(saveSchedulingLinkAsyncAction(panelId, draftLink));
      const availability = await dispatch(
        fetchSchedulingLinkAvailabilityAsyncAction({
          linkId: schedulingLink.id,
          rangeStart: Date.now(),
          rangeEnd: Math.max(max(selectedSlots.map((slot) => slot.end)) || 0, Date.now() + 1),
        }),
      );
      const hasAvailability = availability.timeSlots.length > 0;
      const availabilityStatus = hasAvailability
        ? TrueAvailabilityStatusesEnum.HAS_AVAILABILITY
        : TrueAvailabilityStatusesEnum.NO_AVAILABILITY;
      dispatch(updateTrueAvailabilityStatusAction(panelId, availabilityStatus));
      return {hasAvailability, availability, schedulingLink};
    } catch (error) {
      dispatch(updateTrueAvailabilityStatusAction(panelId, TrueAvailabilityStatusesEnum.UNKNOWN));
      return {hasAvailability: false};
    }
  };
}

export function saveSchedulingLinkAsyncAction(
  panelId: CalendarsPanelId,
  link: DraftSchedulingLinkModel | SchedulingLinkModel,
): ThunkAsyncAction<SchedulingLinkModel> {
  return async (dispatch) => {
    await dispatch(maybeSetServerTimezoneAsyncAction());

    // If a scheduling link already exists, update it.
    // Otherwise, create a new one.
    const schedulingLink = isSavedSchedulingLink(link)
      ? await dispatch(updateSchedulingLinkAsyncAction(link))
      : await dispatch(createSchedulingLinkAsyncAction(link));

    // Update the panel state.
    dispatch(updateDraftSchedulingLinkAction(panelId, schedulingLink));

    return schedulingLink;
  };
}

function maybeSetServerTimezoneAsyncAction(): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const calendarTimezone = getCalendarTimezone(getState());
    if (calendarTimezone) {
      // No need to do anything if the timezone is already set.
      return;
    }

    await dispatch(updateCalendarTimezoneAction(DateTime.local().zoneName));
  };
}

/*
 * Helpers.
 */

type InitialSchedulingLinkData = Partial<DraftSchedulingLinkModel> &
  Pick<DraftSchedulingLinkModel, 'calendarVersionId'>;
export function buildDraftSchedulingLink(data: InitialSchedulingLinkData): DraftSchedulingLinkModel {
  const defaultSchedulingLink = {
    id: undefined,
    slug: '',
    name: '',
    availability: {
      type: SchedulingLinkAvailabilityTypesEnum.STANDARD,
      blocks: [],
    },
    isShared: false,
    isOneTimeLink: false,
    isCalendarEventReschedulingLink: false,
    errorType: undefined,
    meetingProperties: {
      summary: '',
      durationMinutes: 30,
      conferenceType: undefined,
      location: '',
      description: '',
      additionalGuests: [],
      questions: [],
    },
    updatedAt: Date.now(),
    doubleBookingCalendarVersions: [data.calendarVersionId],
    startTimeIncrements: 30,
    meetingBufferMinutes: undefined,
    advanceNoticeMinutes: undefined,
    displayName: '',
    greeting: '',
    bookingPageUrl: '',
    color: undefined,
  };

  return {
    ...defaultSchedulingLink,
    ...data,
  };
}

/*
 * Union type.
 */

export type CalendarsPanelSchedulingAction =
  | UpdateTrueAvailabilityStatusAction
  | UpdateOrganizerAddressAction
  | UpdateDraftSchedulingLinkAction
  | UpdateDraftMeetingPropertiesAction
  | UpdateDraftMeetingDurationAction
  | AddAdditionalGuestsAction
  | FetchSchedulingLinkAction
  | FetchSchedulingLinkCompletedAction
  | FetchSchedulingLinkFailedAction;
